import { Minister } from "./minister";
import { User } from "./user";

// Credential Request model matching Supabase schema
export interface CredentialRequest {
  id: string;
  code: string | null;
  year: number | null;
  region_id: string | null;
  district_id: string | null;
  credential_type: string | null;
  status: string | null;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  printed_at: string | null;
  delivered_at: string | null;
  created_by: string | null;
  approved_by: string | null;
  rejected_by: string | null;
  printed_by: string | null;
  delivered_by: string | null;
  notes: string | null;
  observations: string | null;

  institution_id?: string | null;

  // Populated from joins (optional)
  region?: {
    id: string;
    name: string | null;
    code: string | null;
  };
  district?: {
    id: string;
    name: string | null;
    code: string | null;
  };
  ministers?: Minister[];
  minister_count?: number | Array<{ count: number }>;
  
  // User relationships (optional)
  creator?: User;
  approver?: User;
  rejecter?: User;
  printer?: User;
  deliverer?: User;
}

// Junction table model
export interface CredentialRequestMinister {
  id: number;
  credential_request_id: string;
  minister_id: string;
}

export type CredentialRequestInput = Omit<CredentialRequest, 'id' | 'created_at' | 'region' | 'district' | 'ministers' | 'minister_count' | 'creator' | 'approver' | 'rejecter' | 'printer' | 'deliverer'>;

// Status enum for type safety
export enum CredentialRequestStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  PRINTED = 'PRINTED',
  DELIVERED = 'DELIVERED',
  REJECTED = 'REJECTED',
}

// Credential type enum for type safety
export enum CredentialType {
  MINISTERIAL = 'MINISTERIAL',
  FUNCTION = 'FUNCTION',
  INSTITUTION = 'INSTITUTION',
}

export const CREDENTIAL_TYPES = {
  MINISTERIAL: 'MINISTERIAL' as const,
  FUNCTION: 'FUNCTION' as const,
  INSTITUTION: 'INSTITUTION' as const,
};

// Helper to check if status is "active" (blocks minister from being in multiple requests)
export function isActiveStatus(status: string | null): boolean {
  if (!status) return false;
  const activeStatuses: string[] = [
    CredentialRequestStatus.SUBMITTED,
    CredentialRequestStatus.APPROVED,
    CredentialRequestStatus.PRINTED,
    CredentialRequestStatus.DELIVERED,
  ];
  return activeStatuses.includes(status);
}

// Helper to check if request can be edited
export function canEditRequest(status: string | null): boolean {
  if (!status) return true;
  return status === CredentialRequestStatus.DRAFT || status === CredentialRequestStatus.REJECTED;
}

// Get status badge color
export function getStatusColor(status: string | null): string {
  switch (status) {
    case CredentialRequestStatus.DRAFT:
      return 'gray';
    case CredentialRequestStatus.SUBMITTED:
      return 'blue';
    case CredentialRequestStatus.APPROVED:
      return 'green';
    case CredentialRequestStatus.PRINTED:
      return 'purple';
    case CredentialRequestStatus.DELIVERED:
      return 'teal';
    case CredentialRequestStatus.REJECTED:
      return 'red';
    default:
      return 'gray';
  }
}

// Get status display name in Spanish
export function getStatusDisplayName(status: string | null): string {
  switch (status) {
    case CredentialRequestStatus.DRAFT:
      return 'Borrador';
    case CredentialRequestStatus.SUBMITTED:
      return 'Enviada';
    case CredentialRequestStatus.APPROVED:
      return 'Aprobada';
    case CredentialRequestStatus.PRINTED:
      return 'Impresa';
    case CredentialRequestStatus.DELIVERED:
      return 'Entregada';
    case CredentialRequestStatus.REJECTED:
      return 'Observada';
    default:
      return 'Desconocido';
  }
}
